import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { PageTitle, MasterCrudPage } from "@/components/common/Crud";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { THEMES } from "@/lib/themes";
import {
  clearRolePermissionsCache,
  findRolePermissionEntry,
  hasAnyPermission,
  loadRolePermissions,
  normalizePermissionKey,
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
  resolvePermissionSet,
} from "@/lib/permissions";
import {
  Check,
  Building2,
  Download,
  Hash,
  KeyRound,
  ShieldCheck,
  Phone,
  CreditCard,
} from "lucide-react";
import { FileUploader, buildFileUrl } from "@/components/common/FileUploader";

function SettingBlock({ keyName, fields, testid, extra, footerExtra }) {
  const [data, setData] = useState({ value: {} });
  const load = async () => {
    const { data } = await api.get(`/settings/${keyName}`);
    setData(data || { value: {} });
  };
  useEffect(() => {
    load();
  }, [keyName]);
  const set = (k, v) =>
    setData((d) => ({ ...d, value: { ...(d.value || {}), [k]: v } }));
  const save = async () => {
    await api.put(`/settings/${keyName}`, { value: data.value || {} });
    toast.success("Saved");
  };
  const v = data.value || {};
  return (
    <div
      className="border border-slate-200 bg-white rounded-md p-4 md:p-6 shadow-sm"
      data-testid={`block-${testid}`}
    >
      {extra && extra(v, set)}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {fields.map(([k, l, type, opts]) => (
          <div
            key={k}
            className={type === "textarea" ? "md:col-span-2 lg:col-span-3" : ""}
          >
            <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
              {l}
            </Label>
            {type === "switch" ? (
              <div className="h-9 flex items-center gap-2">
                <Switch
                  checked={!!v[k]}
                  onCheckedChange={(c) => set(k, c)}
                  data-testid={`set-${k}`}
                />
                <span className="text-xs text-slate-600">
                  {v[k] ? "Enabled" : "Disabled"}
                </span>
              </div>
            ) : type === "textarea" ? (
              <textarea
                rows={2}
                value={v[k] || ""}
                onChange={(e) => set(k, e.target.value)}
                className="w-full rounded-md border border-slate-300 text-sm px-2 py-1.5"
                data-testid={`set-${k}`}
              />
            ) : type === "select" ? (
              <select
                value={v[k] || ""}
                onChange={(e) => set(k, e.target.value)}
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                data-testid={`set-${k}`}
              >
                {(opts || []).map((o) => (
                  <option key={o} value={o}>
                    {o.toUpperCase()}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                type={type || "text"}
                value={v[k] ?? ""}
                onChange={(e) =>
                  set(
                    k,
                    type === "number" ? Number(e.target.value) : e.target.value,
                  )
                }
                className="h-9 text-xs"
                data-testid={`set-${k}`}
              />
            )}
          </div>
        ))}
      </div>
      <Button
        onClick={save}
        className="mt-4 erp-primary"
        data-testid={`save-${keyName}`}
      >
        Save Changes
      </Button>
      {footerExtra && footerExtra(v, set)}
    </div>
  );
}

function SchoolInfoBlock() {
  const [backupBusy, setBackupBusy] = useState(false);

  const downloadDbBackup = async () => {
    setBackupBusy(true);
    try {
      const response = await api.get("/settings/database-backup", {
        responseType: "blob",
      });

      const blob = response?.data;
      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error("Empty backup response");
      }

      const header = response.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(header);
      const fileName = match?.[1] || `database-backup-${Date.now()}.sql`;

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);

      toast.success("Database backup downloaded");
    } catch (error) {
      const msg =
        error?.response?.data?.detail ||
        error?.message ||
        "Backup download failed";
      toast.error(msg);
    }
    setBackupBusy(false);
  };

  return (
    <SettingBlock
      keyName="school_info"
      testid="school"
      extra={(v, set) => (
        <div className="mb-4 pb-4 border-b border-slate-200">
          <Label className="text-xs font-semibold text-slate-700 mb-2 block">
            School Logo
          </Label>
          <div className="flex items-start gap-3">
            {v.logo && (
              <img
                src={buildFileUrl(v.logo)}
                alt="logo"
                className="w-16 h-16 object-contain border border-slate-200 rounded bg-white"
              />
            )}
            <div className="flex-1 max-w-sm">
              <FileUploader
                value={v.logo_file}
                onChange={(f) => {
                  set("logo_file", f);
                  set("logo", f?.id);
                }}
                accept=".jpg,.jpeg,.png,.webp,.svg"
                tag="school_logo"
                testid="logo-upload"
              />
            </div>
          </div>
        </div>
      )}
      footerExtra={() => (
        <div className="mt-3">
          <Button
            type="button"
            variant="outline"
            onClick={downloadDbBackup}
            disabled={backupBusy}
            data-testid="download-db-backup"
          >
            <Download className="w-4 h-4 mr-2" />
            {backupBusy ? "Preparing Backup..." : "Download Database Backup"}
          </Button>
        </div>
      )}
      fields={[
        ["school_name", "School Name"],
        ["motto", "Motto / Tagline"],
        ["website", "Website"],
        ["principal_name", "Principal"],
        ["board", "Board (CBSE/ICSE/State)"],
        ["affiliation_no", "Affiliation No."],
        ["udise_code", "UDISE+ Code"],
        ["registration_no", "Registration No."],
        ["gst_no", "GST No."],
        ["pan_no", "PAN No."],
        ["phone", "Phone"],
        ["email", "Email"],
        ["city", "City"],
        ["state", "State"],
        ["pin", "Pincode"],
        ["address", "Address", "textarea"],
      ]}
    />
  );
}

const ID_CARD_FIELD_OPTIONS = [
  ["father_name", "Father's Name"],
  ["mother_name", "Mother's Name"],
  ["dob", "Date of Birth"],
  ["blood_group", "Blood Group"],
  ["address", "Address"],
  ["contact", "Contact Number"],
];

function IdCardSettingsBlock() {
  const [value, setValue] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/id-card/settings");
      setValue(data?.value || {});
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setValue((d) => ({ ...d, [k]: v }));

  const showFields = Array.isArray(value.show_fields) ? value.show_fields : [];
  const toggleField = (key) => {
    const next = showFields.includes(key)
      ? showFields.filter((f) => f !== key)
      : [...showFields, key];
    set("show_fields", next);
  };

  const save = async () => {
    const payload = {
      template_name: value.template_name,
      primary_color: value.primary_color,
      secondary_color: value.secondary_color,
      text_color: value.text_color,
      card_width_mm: value.card_width_mm,
      card_height_mm: value.card_height_mm,
      logo_upload_id: value.logo_upload_id,
      principal_signature_upload_id: value.principal_signature_upload_id,
      principal_name: value.principal_name,
      front_background_upload_id: value.front_background_upload_id,
      front_bg_prelabeled: !!value.front_bg_prelabeled,
      back_background_upload_id: value.back_background_upload_id,
      footer_text: value.footer_text,
      valid_upto: value.valid_upto,
      map_address_url: value.map_address_url,
      show_fields: showFields,
    };
    const { data } = await api.put("/id-card/settings", { value: payload });
    setValue(data?.value || payload);
    toast.success("Saved");
  };

  if (loading) {
    return <div className="text-xs text-slate-500 p-4">Loading…</div>;
  }

  return (
    <div
      className="border border-slate-200 bg-white rounded-md p-4 md:p-6 shadow-sm"
      data-testid="block-idcard"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pb-4 border-b border-slate-200">
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-2 block">
            Logo Override (optional)
          </Label>
          <p className="text-[11px] text-slate-500 mb-2">
            Leave empty to use the School Logo from the School tab.
          </p>
          <div className="flex items-start gap-3">
            {value.logo_upload_id && (
              <img
                src={buildFileUrl(value.logo_upload_id)}
                alt="logo"
                className="w-14 h-14 object-contain border border-slate-200 rounded bg-white"
              />
            )}
            <div className="flex-1 max-w-xs">
              <FileUploader
                value={value.logo_upload_id_file}
                onChange={(f) => {
                  set("logo_upload_id_file", f);
                  set("logo_upload_id", f?.id || null);
                }}
                accept=".jpg,.jpeg,.png,.webp,.svg"
                tag="id_card_logo"
                testid="idcard-logo-upload"
              />
            </div>
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-2 block">
            Principal Signature
          </Label>
          <p className="text-[11px] text-slate-500 mb-2">
            Printed on the back of the card above the principal's name.
          </p>
          <div className="flex items-start gap-3">
            {value.principal_signature_upload_id && (
              <img
                src={buildFileUrl(value.principal_signature_upload_id)}
                alt="signature"
                className="w-24 h-14 object-contain border border-slate-200 rounded bg-white"
              />
            )}
            <div className="flex-1 max-w-xs">
              <FileUploader
                value={value.principal_signature_upload_id_file}
                onChange={(f) => {
                  set("principal_signature_upload_id_file", f);
                  set("principal_signature_upload_id", f?.id || null);
                }}
                accept=".jpg,.jpeg,.png,.webp"
                tag="principal_signature"
                testid="idcard-signature-upload"
              />
            </div>
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-2 block">
            Front Side Background (optional)
          </Label>
          <div className="flex items-start gap-3">
            {value.front_background_upload_id && (
              <img
                src={buildFileUrl(value.front_background_upload_id)}
                alt="front background"
                className="w-14 h-14 object-cover border border-slate-200 rounded bg-white"
              />
            )}
            <div className="flex-1 max-w-xs">
              <FileUploader
                value={value.front_background_upload_id_file}
                onChange={(f) => {
                  set("front_background_upload_id_file", f);
                  set("front_background_upload_id", f?.id || null);
                }}
                accept=".jpg,.jpeg,.png,.webp"
                tag="id_card_front_bg"
                testid="idcard-front-bg-upload"
              />
            </div>
          </div>
          {value.front_background_upload_id && (
            <div className="mt-2 flex items-center gap-2">
              <Switch
                checked={!!value.front_bg_prelabeled}
                onCheckedChange={(c) => set("front_bg_prelabeled", c)}
                data-testid="set-front_bg_prelabeled"
              />
              <span className="text-[11px] text-slate-600">
                This image already has "CLASS / FATHER'S NAME / ..." captions
                printed on it (turn off if it's a plain design with no text)
              </span>
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-2 block">
            Back Side Background (optional)
          </Label>
          <div className="flex items-start gap-3">
            {value.back_background_upload_id && (
              <img
                src={buildFileUrl(value.back_background_upload_id)}
                alt="back background"
                className="w-14 h-14 object-cover border border-slate-200 rounded bg-white"
              />
            )}
            <div className="flex-1 max-w-xs">
              <FileUploader
                value={value.back_background_upload_id_file}
                onChange={(f) => {
                  set("back_background_upload_id_file", f);
                  set("back_background_upload_id", f?.id || null);
                }}
                accept=".jpg,.jpeg,.png,.webp"
                tag="id_card_back_bg"
                testid="idcard-back-bg-upload"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <div>
          <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
            Template Name
          </Label>
          <Input
            className="h-9 text-xs"
            value={value.template_name || ""}
            onChange={(e) => set("template_name", e.target.value)}
            data-testid="set-template_name"
          />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
            Principal Name
          </Label>
          <Input
            className="h-9 text-xs"
            placeholder="Uses School Info principal by default"
            value={value.principal_name || ""}
            onChange={(e) => set("principal_name", e.target.value)}
            data-testid="set-principal_name"
          />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
            Valid Upto
          </Label>
          <Input
            className="h-9 text-xs"
            placeholder="e.g. March 2027"
            value={value.valid_upto || ""}
            onChange={(e) => set("valid_upto", e.target.value)}
            data-testid="set-valid_upto"
          />
        </div>
        <div className="md:col-span-2 lg:col-span-3">
          <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
            Map Address (Google Maps Link)
          </Label>
          <Input
            className="h-9 text-xs"
            placeholder="e.g. https://maps.app.goo.gl/xxxxxxx"
            value={value.map_address_url || ""}
            onChange={(e) => set("map_address_url", e.target.value)}
            data-testid="set-map_address_url"
          />
          <p className="text-[10px] text-slate-500 mt-1">
            The QR code printed on the back of the card opens this location when
            scanned. Leave empty to use the school website instead.
          </p>
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
            Front Header Color
          </Label>
          <Input
            type="color"
            className="h-9 w-full p-1"
            value={value.primary_color || "#12294f"}
            onChange={(e) => set("primary_color", e.target.value)}
            data-testid="set-primary_color"
          />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
            Back Side Color
          </Label>
          <Input
            type="color"
            className="h-9 w-full p-1"
            value={value.secondary_color || "#eef2f9"}
            onChange={(e) => set("secondary_color", e.target.value)}
            data-testid="set-secondary_color"
          />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
            Text Color
          </Label>
          <Input
            type="color"
            className="h-9 w-full p-1"
            value={value.text_color || "#1e293b"}
            onChange={(e) => set("text_color", e.target.value)}
            data-testid="set-text_color"
          />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
            Card Width (mm)
          </Label>
          <Input
            type="number"
            className="h-9 text-xs"
            value={value.card_width_mm ?? 54}
            onChange={(e) => set("card_width_mm", Number(e.target.value))}
            data-testid="set-card_width_mm"
          />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
            Card Height (mm)
          </Label>
          <Input
            type="number"
            className="h-9 text-xs"
            value={value.card_height_mm ?? 86}
            onChange={(e) => set("card_height_mm", Number(e.target.value))}
            data-testid="set-card_height_mm"
          />
        </div>
        <div className="md:col-span-2 lg:col-span-3">
          <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
            Back Side Footer Note
          </Label>
          <textarea
            rows={2}
            className="w-full rounded-md border border-slate-300 text-sm px-2 py-1.5"
            value={value.footer_text || ""}
            onChange={(e) => set("footer_text", e.target.value)}
            data-testid="set-footer_text"
          />
        </div>
      </div>

      <div className="mb-4">
        <Label className="text-[11px] font-semibold text-slate-700 mb-2 block">
          Fields Shown on Card
        </Label>
        <div className="flex flex-wrap gap-3">
          {ID_CARD_FIELD_OPTIONS.map(([key, label]) => (
            <label
              key={key}
              className="flex items-center gap-1.5 text-xs text-slate-700"
            >
              <input
                type="checkbox"
                checked={showFields.includes(key)}
                onChange={() => toggleField(key)}
                data-testid={`set-field-${key}`}
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      <Button
        onClick={save}
        className="erp-primary"
        data-testid="save-idcard-settings"
      >
        Save Changes
      </Button>
    </div>
  );
}

function StorageSettingsBlock() {
  const defaults = {
    driver: "local",
    bucket: "",
    region: "ap-south-1",
    prefix: "school-erp",
    access_key_id: "",
    secret_access_key: "",
    endpoint: "",
    use_path_style: false,
  };

  const [cfg, setCfg] = useState(defaults);

  const load = async () => {
    const { data } = await api.get("/settings/storage_config");
    const value =
      data?.value && typeof data.value === "object" ? data.value : {};
    setCfg({ ...defaults, ...value });
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setCfg((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (cfg.driver === "s3") {
      if (!cfg.bucket || !cfg.access_key_id || !cfg.secret_access_key) {
        toast.error(
          "For AWS S3, Bucket, Access Key ID and Secret Access Key are required",
        );
        return;
      }
    }

    await api.put("/settings/storage_config", { value: cfg });
    toast.success("Storage settings saved");
  };

  return (
    <div
      className="border border-slate-200 bg-white rounded-md p-4 md:p-6 shadow-sm"
      data-testid="storage-block"
    >
      <h3 className="text-sm font-bold erp-text-primary font-serif-display mb-3 pb-2 border-b">
        Upload Storage
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
            Storage Driver
          </Label>
          <select
            value={cfg.driver || "local"}
            onChange={(e) => set("driver", e.target.value)}
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
            data-testid="storage-driver"
          >
            <option value="local">Local Server</option>
            <option value="s3">AWS S3</option>
          </select>
        </div>
      </div>

      {cfg.driver === "local" ? (
        <div
          className="mt-3 text-xs text-slate-600 bg-emerald-50 border border-emerald-200 rounded p-2"
          data-testid="storage-local-note"
        >
          Local selected: files will be saved on your own server under
          writable/uploads.
        </div>
      ) : (
        <div
          className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3"
          data-testid="storage-s3-fields"
        >
          <div>
            <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
              AWS S3 Bucket *
            </Label>
            <Input
              value={cfg.bucket || ""}
              onChange={(e) => set("bucket", e.target.value)}
              className="h-9 text-xs"
              data-testid="s3-bucket"
            />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
              Region
            </Label>
            <Input
              value={cfg.region || ""}
              onChange={(e) => set("region", e.target.value)}
              className="h-9 text-xs"
              data-testid="s3-region"
            />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
              Access Key ID *
            </Label>
            <Input
              value={cfg.access_key_id || ""}
              onChange={(e) => set("access_key_id", e.target.value)}
              className="h-9 text-xs"
              data-testid="s3-access-key"
            />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
              Secret Access Key *
            </Label>
            <Input
              type="password"
              value={cfg.secret_access_key || ""}
              onChange={(e) => set("secret_access_key", e.target.value)}
              className="h-9 text-xs"
              data-testid="s3-secret-key"
            />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
              Prefix
            </Label>
            <Input
              value={cfg.prefix || ""}
              onChange={(e) => set("prefix", e.target.value)}
              className="h-9 text-xs"
              data-testid="s3-prefix"
            />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
              Custom Endpoint (optional)
            </Label>
            <Input
              value={cfg.endpoint || ""}
              onChange={(e) => set("endpoint", e.target.value)}
              className="h-9 text-xs"
              data-testid="s3-endpoint"
              placeholder="https://s3.amazonaws.com"
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-2 h-9">
            <Switch
              checked={!!cfg.use_path_style}
              onCheckedChange={(v) => set("use_path_style", v)}
              data-testid="s3-path-style"
            />
            <span className="text-xs text-slate-600">
              Use path style endpoint
            </span>
          </div>
        </div>
      )}

      <Button
        onClick={save}
        className="mt-4 erp-primary"
        data-testid="save-storage-config"
      >
        Save Storage Settings
      </Button>
    </div>
  );
}

/* Auto Generation block — Admission No + Staff ID */
function AutoGenBlock() {
  const [adm, setAdm] = useState({});
  const [staff, setStaff] = useState({});
  const load = async () => {
    const [a, s] = await Promise.all([
      api.get("/settings/admission_config"),
      api.get("/settings/staff_id_config"),
    ]);
    setAdm(
      a.data?.value || {
        enabled: true,
        prefix: "ADM-",
        digits: 5,
        start_from: 1,
      },
    );
    setStaff(
      s.data?.value || {
        enabled: true,
        prefix: "STF-",
        digits: 4,
        start_from: 1,
      },
    );
  };
  useEffect(() => {
    load();
  }, []);
  const save = async () => {
    await api.put("/settings/admission_config", { value: adm });
    await api.put("/settings/staff_id_config", { value: staff });
    toast.success("Auto-generation rules saved");
  };
  return (
    <div className="space-y-4" data-testid="autogen-block">
      <div className="border border-slate-200 bg-white rounded-md p-4 md:p-6 shadow-sm">
        <h3 className="text-sm font-bold erp-text-primary font-serif-display flex items-center gap-2 mb-3 pb-2 border-b">
          <Hash className="w-4 h-4" /> Student Admission No. Auto Generation
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <Switch
              checked={!!adm.enabled}
              onCheckedChange={(c) => setAdm({ ...adm, enabled: c })}
              data-testid="adm-enabled"
            />
            <span className="text-sm font-semibold">
              {adm.enabled ? "Enabled" : "Disabled"}
            </span>
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              Admission No Prefix
            </Label>
            <Input
              value={adm.prefix ?? ""}
              onChange={(e) => setAdm({ ...adm, prefix: e.target.value })}
              placeholder="ADM-"
              className="h-9"
              data-testid="adm-prefix"
              disabled={!adm.enabled}
            />
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              Admission No Digits *
            </Label>
            <select
              value={adm.digits ?? 5}
              onChange={(e) =>
                setAdm({ ...adm, digits: Number(e.target.value) })
              }
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              data-testid="adm-digits"
              disabled={!adm.enabled}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              Admission Start From *
            </Label>
            <Input
              type="number"
              value={adm.start_from ?? 1}
              onChange={(e) =>
                setAdm({ ...adm, start_from: Number(e.target.value) })
              }
              className="h-9"
              data-testid="adm-start"
              disabled={!adm.enabled}
            />
          </div>
        </div>
        <div className="mt-3 text-xs text-slate-500 bg-indigo-50 rounded p-2 border border-indigo-100">
          Preview:{" "}
          <span className="font-mono font-bold erp-text-primary">
            {adm.prefix || ""}
            {String(adm.start_from || 1).padStart(adm.digits || 5, "0")}
          </span>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-md p-4 md:p-6 shadow-sm">
        <h3 className="text-sm font-bold erp-text-primary font-serif-display flex items-center gap-2 mb-3 pb-2 border-b">
          <Hash className="w-4 h-4" /> Staff ID Auto Generation
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <Switch
              checked={!!staff.enabled}
              onCheckedChange={(c) => setStaff({ ...staff, enabled: c })}
              data-testid="staffid-enabled"
            />
            <span className="text-sm font-semibold">
              {staff.enabled ? "Enabled" : "Disabled"}
            </span>
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              Staff ID Prefix *
            </Label>
            <Input
              value={staff.prefix ?? ""}
              onChange={(e) => setStaff({ ...staff, prefix: e.target.value })}
              placeholder="MM/"
              className="h-9"
              data-testid="staffid-prefix"
              disabled={!staff.enabled}
            />
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              Staff No Digits *
            </Label>
            <select
              value={staff.digits ?? 4}
              onChange={(e) =>
                setStaff({ ...staff, digits: Number(e.target.value) })
              }
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              data-testid="staffid-digits"
              disabled={!staff.enabled}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              Staff ID Start From *
            </Label>
            <Input
              type="number"
              value={staff.start_from ?? 1}
              onChange={(e) =>
                setStaff({ ...staff, start_from: Number(e.target.value) })
              }
              className="h-9"
              data-testid="staffid-start"
              disabled={!staff.enabled}
            />
          </div>
        </div>
        <div className="mt-3 text-xs text-slate-500 bg-amber-50 rounded p-2 border border-amber-100">
          Preview:{" "}
          <span className="font-mono font-bold text-amber-700">
            {staff.prefix || ""}
            {String(staff.start_from || 1).padStart(staff.digits || 4, "0")}
          </span>
        </div>
      </div>

      <Button onClick={save} className="erp-primary" data-testid="save-autogen">
        Save Auto Generation Rules
      </Button>
    </div>
  );
}

function labelize(key) {
  return normalizePermissionKey(key).replace(/_/g, " ");
}

function ensurePermissionShape(roleEntry, moduleKey, submoduleKey) {
  const modulePerm = roleEntry[moduleKey];
  if (submoduleKey) {
    return {
      ...(roleEntry || {}),
      [moduleKey]: {
        ...(modulePerm &&
        typeof modulePerm === "object" &&
        !Array.isArray(modulePerm)
          ? modulePerm
          : {}),
        [submoduleKey]: {
          ...(modulePerm?.[submoduleKey] || {}),
        },
      },
    };
  }

  return {
    ...(roleEntry || {}),
    [moduleKey]: {
      ...(modulePerm &&
      typeof modulePerm === "object" &&
      !Array.isArray(modulePerm)
        ? modulePerm
        : {}),
    },
  };
}

function RolePermissions() {
  const [perms, setPerms] = useState({});
  const [roles, setRoles] = useState([]);
  const [activeRole, setActiveRole] = useState("teacher");
  const [query, setQuery] = useState("");
  useEffect(() => {
    (async () => {
      const [p, r] = await Promise.all([
        loadRolePermissions(api),
        api.get("/roles"),
      ]);
      setPerms(p || {});
      const rs = r.data?.items || [];
      setRoles(rs);
      if (rs.length && !rs.find((x) => x.name === activeRole)) {
        setActiveRole(rs[0].name);
      }
    })();
  }, []);

  const updateRolePerms = (updater) => {
    setPerms((current) => {
      const roleKey = activeRole;
      const currentRole = current[roleKey] || {};
      return {
        ...current,
        [roleKey]: updater(currentRole),
      };
    });
  };

  const toggleModuleAction = (moduleKey, action) => {
    updateRolePerms((roleEntry) => {
      const nextRole = ensurePermissionShape(roleEntry, moduleKey);
      nextRole[moduleKey][action] = !nextRole[moduleKey][action];
      return nextRole;
    });
  };

  const toggleModuleAll = (moduleKey, val) => {
    updateRolePerms((roleEntry) => {
      const nextRole = ensurePermissionShape(roleEntry, moduleKey);
      for (const action of PERMISSION_ACTIONS) {
        nextRole[moduleKey][action] = val;
      }
      return nextRole;
    });
  };

  const toggleSubmoduleAction = (moduleKey, submoduleKey, action) => {
    updateRolePerms((roleEntry) => {
      const nextRole = ensurePermissionShape(
        roleEntry,
        moduleKey,
        submoduleKey,
      );
      nextRole[moduleKey][submoduleKey][action] =
        !nextRole[moduleKey][submoduleKey][action];
      return nextRole;
    });
  };

  const toggleSubmoduleAll = (moduleKey, submoduleKey, val) => {
    updateRolePerms((roleEntry) => {
      const nextRole = ensurePermissionShape(
        roleEntry,
        moduleKey,
        submoduleKey,
      );
      for (const action of PERMISSION_ACTIONS) {
        nextRole[moduleKey][submoduleKey][action] = val;
      }
      return nextRole;
    });
  };

  const save = async () => {
    await api.put("/settings/role_permissions", { value: perms });
    clearRolePermissionsCache();
    toast.success("Role permissions saved");
  };

  const roleEntry = findRolePermissionEntry(perms, activeRole) || {};
  const filteredModules = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return PERMISSION_MODULES;
    return PERMISSION_MODULES.filter((moduleItem) => {
      if (moduleItem.label.toLowerCase().includes(needle)) return true;
      return (moduleItem.children || []).some((child) =>
        child.label.toLowerCase().includes(needle),
      );
    });
  }, [query]);

  const summary = useMemo(() => {
    let visibleModules = 0;
    let restrictedDeletes = 0;

    for (const moduleItem of PERMISSION_MODULES) {
      const modulePerm = resolvePermissionSet(roleEntry, moduleItem.key, null);
      if (hasAnyPermission(modulePerm)) {
        visibleModules += 1;
      }
      if (modulePerm.delete) {
        restrictedDeletes += 1;
      }
      for (const child of moduleItem.children || []) {
        const childPerm = resolvePermissionSet(
          roleEntry,
          moduleItem.key,
          child.key,
        );
        if (childPerm.delete) {
          restrictedDeletes += 1;
        }
      }
    }

    return { visibleModules, restrictedDeletes };
  }, [roleEntry]);

  return (
    <div
      className="border border-slate-200 bg-white rounded-md p-4 md:p-6 shadow-sm"
      data-testid="role-perms-block"
    >
      <div className="mb-3">
        <h3 className="text-sm font-bold erp-text-primary font-serif-display flex items-center gap-2 mb-1">
          <ShieldCheck className="w-4 h-4" /> Role-wise Permission Matrix
        </h3>
        <p className="text-xs text-slate-500">
          Manage roles from <strong>Role Master</strong> tab. Student/Parent
          roles are managed in the Parent mobile app.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px] gap-3 mb-4">
        <div className="flex flex-wrap gap-2 items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="text-xs font-semibold text-slate-600">
            Quick Summary
          </span>
          <span className="text-[11px] px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-600">
            {summary.visibleModules} modules visible
          </span>
          <span className="text-[11px] px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-600">
            {summary.restrictedDeletes} delete grants active
          </span>
        </div>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search module or sub-feature"
          className="h-10"
          data-testid="permissions-search"
        />
      </div>
      <div className="flex flex-wrap gap-1 mb-3 border-b border-slate-200">
        {roles.map((r) => (
          <button
            key={r.id}
            onClick={() => setActiveRole(r.name)}
            data-testid={`role-tab-${r.name}`}
            className={`px-3 py-1.5 text-xs font-semibold capitalize border-b-2 -mb-px ${activeRole === r.name ? "border-[var(--erp-accent)] erp-text-primary" : "border-transparent text-slate-600 hover:text-slate-900"}`}
          >
            {r.label || r.name}
          </button>
        ))}
      </div>
      <div className="space-y-4">
        {filteredModules.map((moduleItem) => {
          const moduleKey = normalizePermissionKey(moduleItem.key);
          const modulePerm = resolvePermissionSet(roleEntry, moduleKey, null);
          const moduleAll = PERMISSION_ACTIONS.every(
            (action) => modulePerm[action],
          );

          return (
            <div
              key={moduleKey}
              className="rounded-xl border border-slate-200 overflow-hidden"
            >
              <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-800">
                    {moduleItem.label}
                  </div>
                  <div className="text-xs text-slate-500">
                    Top navigation access and base actions for this module.
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {PERMISSION_ACTIONS.map((action) => (
                    <label
                      key={`${moduleKey}-${action}`}
                      className="flex items-center gap-2 text-xs font-medium text-slate-700"
                    >
                      <span className="capitalize">{action}</span>
                      <Switch
                        checked={!!modulePerm[action]}
                        onCheckedChange={() =>
                          toggleModuleAction(moduleKey, action)
                        }
                        data-testid={`rp-${activeRole}-${moduleKey}-${action}`}
                      />
                    </label>
                  ))}
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-800">
                    <span>All</span>
                    <Switch
                      checked={moduleAll}
                      onCheckedChange={(checked) =>
                        toggleModuleAll(moduleKey, checked)
                      }
                      data-testid={`rp-${activeRole}-${moduleKey}-all`}
                    />
                  </label>
                </div>
              </div>

              {(moduleItem.children || []).length > 0 && (
                <div className="p-4 space-y-2 bg-white">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                    Sub-features
                  </div>
                  {(moduleItem.children || []).map((child) => {
                    const childKey = normalizePermissionKey(child.key);
                    const childPerm = resolvePermissionSet(
                      roleEntry,
                      moduleKey,
                      childKey,
                    );
                    const childAll = PERMISSION_ACTIONS.every(
                      (action) => childPerm[action],
                    );

                    return (
                      <div
                        key={`${moduleKey}-${childKey}`}
                        className="rounded-lg border border-slate-200 px-3 py-2 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-2"
                      >
                        <div>
                          <div className="text-sm font-medium text-slate-700">
                            {child.label}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            Restrict exactly what this role can do in{" "}
                            {child.label.toLowerCase()}.
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          {PERMISSION_ACTIONS.map((action) => (
                            <label
                              key={`${moduleKey}-${childKey}-${action}`}
                              className="flex items-center gap-2 text-xs font-medium text-slate-700"
                            >
                              <span className="capitalize">{action}</span>
                              <Switch
                                checked={!!childPerm[action]}
                                onCheckedChange={() =>
                                  toggleSubmoduleAction(
                                    moduleKey,
                                    childKey,
                                    action,
                                  )
                                }
                                data-testid={`rp-${activeRole}-${moduleKey}-${childKey}-${action}`}
                              />
                            </label>
                          ))}
                          <label className="flex items-center gap-2 text-xs font-semibold text-slate-800">
                            <span>All</span>
                            <Switch
                              checked={childAll}
                              onCheckedChange={(checked) =>
                                toggleSubmoduleAll(moduleKey, childKey, checked)
                              }
                              data-testid={`rp-${activeRole}-${moduleKey}-${childKey}-all`}
                            />
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {filteredModules.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
            No module or sub-feature matched your search.
          </div>
        )}
      </div>
      <Button
        onClick={save}
        className="mt-4 erp-primary"
        data-testid="save-permissions"
      >
        Save Permissions
      </Button>
    </div>
  );
}

function ThemePicker() {
  const { themeKey, updateTheme } = useAuth();
  return (
    <div className="border border-slate-200 bg-white rounded-md p-4 md:p-6 shadow-sm">
      <div className="mb-4 flex items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">
            Theme Gallery
          </h3>
          <p className="text-xs text-slate-500">
            Pick a theme — applies instantly.
          </p>
        </div>
        <div className="text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2.5 py-1">
          {Object.keys(THEMES).length} options
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {Object.entries(THEMES).map(([k, t]) => {
          const active = themeKey === k;
          return (
            <button
              key={k}
              data-testid={`theme-${k}`}
              onClick={() => {
                updateTheme(k);
                toast.success(`Theme: ${t.label}`);
              }}
              className={`theme-chip group relative text-left rounded-xl overflow-hidden border transition-all duration-200 ${
                active
                  ? "border-[var(--erp-accent)] shadow-lg ring-2 ring-[var(--erp-accent)]/25"
                  : "border-slate-200 hover:shadow-lg hover:-translate-y-0.5"
              }`}
            >
              <div className="h-28 relative" style={{ background: t.gradient }}>
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_16%,rgba(255,255,255,0.22),transparent_42%)]" />
                <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                  <div className="text-white text-[11px] font-semibold uppercase tracking-wide">
                    {active ? "Active Theme" : "Preview"}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {[t.topbar, t.primary, t.accent].map((color, idx) => (
                      <span
                        key={`${k}-dot-${idx}`}
                        className="w-2.5 h-2.5 rounded-full ring-1 ring-white/60"
                        style={{ background: color }}
                      />
                    ))}
                  </div>
                </div>
                <div className="absolute bottom-3 left-3 text-white">
                  <div className="text-sm font-semibold font-serif-display leading-tight">
                    {t.label}
                  </div>
                  <div className="text-[11px] text-white/85">
                    {t.description}
                  </div>
                </div>
                <div
                  className="absolute bottom-3 right-3 w-8 h-8 rounded-full border-2 border-white/90"
                  style={{ background: t.accent }}
                />
                {active && (
                  <div className="absolute top-3 right-3 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm">
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                )}
              </div>
              <div className="p-3 bg-white">
                <div className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
                  <div
                    className="h-5 px-2 flex items-center text-[10px] font-semibold"
                    style={{ background: t.topbar, color: t.topbarFg }}
                  >
                    SCHOOL ERP
                  </div>
                  <div className="p-2">
                    <div
                      className="h-2.5 rounded-full mb-1.5"
                      style={{ background: t.border }}
                    />
                    <div
                      className="h-2.5 w-2/3 rounded-full mb-2"
                      style={{ background: t.border }}
                    />
                    <div
                      className="inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold"
                      style={{ background: t.primary, color: t.primaryFg }}
                    >
                      Button
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Settings() {
  return (
    <div data-testid="settings-page">
      <PageTitle
        title="Settings"
        subtitle="School info · Auto-generation · Storage · Branches · Sessions · Fees · Roles · Themes"
      />
      <Tabs defaultValue="school">
        <TabsList className="flex-wrap h-auto p-1">
          <TabsTrigger value="school" data-testid="set-tab-school">
            <Building2 className="w-3.5 h-3.5 mr-1.5" />
            School
          </TabsTrigger>
          <TabsTrigger value="autogen" data-testid="set-tab-autogen">
            <Hash className="w-3.5 h-3.5 mr-1.5" />
            Auto Generation
          </TabsTrigger>
          <TabsTrigger value="storage" data-testid="set-tab-storage">
            Storage
          </TabsTrigger>
          <TabsTrigger value="idcard" data-testid="set-tab-idcard">
            <CreditCard className="w-3.5 h-3.5 mr-1.5" />
            ID Card
          </TabsTrigger>
          <TabsTrigger value="branches" data-testid="set-tab-branches">
            Branches
          </TabsTrigger>
          <TabsTrigger value="sessions" data-testid="set-tab-sessions">
            Sessions
          </TabsTrigger>
          <TabsTrigger value="fees" data-testid="set-tab-fees">
            Fees
          </TabsTrigger>
          <TabsTrigger value="modules" data-testid="set-tab-modules">
            Modules
          </TabsTrigger>
          <TabsTrigger value="frontoffice" data-testid="set-tab-frontoffice">
            <Phone className="w-3.5 h-3.5 mr-1.5" />
            Front Office
          </TabsTrigger>
          <TabsTrigger value="roles" data-testid="set-tab-roles">
            <KeyRound className="w-3.5 h-3.5 mr-1.5" />
            Role Master
          </TabsTrigger>
          <TabsTrigger value="permissions" data-testid="set-tab-permissions">
            <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
            Permissions
          </TabsTrigger>
          <TabsTrigger value="themes" data-testid="set-tab-themes">
            Themes
          </TabsTrigger>
        </TabsList>
        <TabsContent value="school" className="mt-3">
          <SchoolInfoBlock />
        </TabsContent>
        <TabsContent value="autogen" className="mt-3">
          <AutoGenBlock />
        </TabsContent>
        <TabsContent value="storage" className="mt-3">
          <StorageSettingsBlock />
        </TabsContent>
        <TabsContent value="idcard" className="mt-3">
          <IdCardSettingsBlock />
        </TabsContent>
        <TabsContent value="branches" className="mt-3">
          <MasterCrudPage
            title="Branches"
            endpoint="/branches"
            testid="branches"
            fields={[
              { key: "name", label: "Branch Name", required: true },
              { key: "code", label: "Branch Code", required: true },
              { key: "phone", label: "Phone" },
              { key: "email", label: "Email" },
              {
                key: "address",
                label: "Address",
                type: "textarea",
                full: true,
              },
              { key: "principal_name", label: "Principal" },
              { key: "udise_code", label: "UDISE+ Code" },
            ]}
            columns={[
              { key: "name", label: "Name" },
              { key: "code", label: "Code" },
              { key: "phone", label: "Phone" },
              { key: "principal_name", label: "Principal" },
            ]}
          />
        </TabsContent>
        <TabsContent value="sessions" className="mt-3">
          <MasterCrudPage
            title="Academic Sessions"
            endpoint="/sessions"
            testid="sessions"
            fields={[
              {
                key: "name",
                label: "Session Name (e.g. 2025-2026)",
                required: true,
              },
              { key: "start_date", label: "Start Date", type: "date" },
              { key: "end_date", label: "End Date", type: "date" },
              {
                key: "is_active",
                label: "Active",
                type: "select",
                options: [
                  { value: "1", label: "Yes" },
                  { value: "0", label: "No" },
                ],
              },
            ]}
          />
        </TabsContent>
        <TabsContent value="fees" className="mt-3">
          <SettingBlock
            keyName="fee_settings"
            testid="fees"
            fields={[
              ["receipt_prefix", "Receipt Prefix"],
              ["auto_receipt_number", "Auto Receipt #", "switch"],
              ["late_fee", "Late Fee", "number"],
              ["fine_per_day", "Fine Per Day", "number"],
              ["gst_enabled", "GST Invoice Format", "switch"],
              ["upi_id", "UPI ID (for receipt QR)"],
              ["bank_name", "Bank Name (on receipt)"],
              ["account_no", "Account No (on receipt)"],
              ["ifsc", "IFSC Code"],
            ]}
          />
        </TabsContent>
        <TabsContent value="modules" className="mt-3">
          <ModulesEnabled />
        </TabsContent>
        <TabsContent value="frontoffice" className="mt-3">
          <FrontOfficeMasters />
        </TabsContent>
        <TabsContent value="roles" className="mt-3">
          <MasterCrudPage
            title="Role Master"
            endpoint="/roles"
            testid="roles"
            fields={[
              {
                key: "name",
                label: "Role Key (lowercase, no spaces — e.g. exam_controller)",
                required: true,
              },
              {
                key: "label",
                label: "Display Label (e.g. Exam Controller)",
                required: true,
              },
              {
                key: "description",
                label: "Description",
                type: "textarea",
                full: true,
              },
              {
                key: "is_system",
                label: "System Role (read-only, internal use)",
                type: "select",
                options: [
                  { value: "false", label: "No (Custom)" },
                  { value: "true", label: "Yes" },
                ],
              },
            ]}
            columns={[
              { key: "label", label: "Role" },
              { key: "name", label: "Key" },
              {
                key: "is_system",
                label: "System",
                render: (r) => (r.is_system ? "Yes" : "Custom"),
              },
              { key: "description", label: "Description" },
            ]}
          />
        </TabsContent>
        <TabsContent value="permissions" className="mt-3">
          <RolePermissions />
        </TabsContent>
        <TabsContent value="themes" className="mt-3">
          <ThemePicker />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ModulesEnabled() {
  const [data, setData] = useState({ value: {} });
  const load = async () => {
    const { data } = await api.get(`/settings/modules_enabled`);
    setData(data || { value: {} });
  };
  useEffect(() => {
    load();
  }, []);
  const v = data.value || {};
  const toggle = (m) =>
    setData((d) => ({
      ...d,
      value: { ...(d.value || {}), [m]: !d.value?.[m] },
    }));
  const save = async () => {
    await api.put(`/settings/modules_enabled`, { value: data.value || {} });
    toast.success("Saved");
  };
  return (
    <div className="border border-slate-200 bg-white rounded-md p-4 md:p-6 shadow-sm">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {PERMISSION_MODULES.map((moduleItem) => (
          <div
            key={moduleItem.key}
            className="flex items-center justify-between gap-2 py-2 px-3 rounded border border-slate-200"
          >
            <span className="capitalize text-sm font-medium text-slate-700">
              {moduleItem.label || labelize(moduleItem.key)}
            </span>
            <Switch
              checked={!!v[moduleItem.key]}
              onCheckedChange={() => toggle(moduleItem.key)}
              data-testid={`mod-${moduleItem.key}`}
            />
          </div>
        ))}
      </div>
      <Button
        onClick={save}
        className="mt-4 erp-primary"
        data-testid="save-modules"
      >
        Save
      </Button>
    </div>
  );
}

function FrontOfficeMasters() {
  const [tab, setTab] = useState("sources");
  const tabs = [
    {
      key: "sources",
      label: "Enquiry Sources",
      endpoint: "/enquiry-sources",
      testid: "fo-sources",
    },
    {
      key: "references",
      label: "Enquiry References",
      endpoint: "/enquiry-references",
      testid: "fo-references",
    },
    {
      key: "complaints",
      label: "Complaint Types",
      endpoint: "/complaint-types",
      testid: "fo-complaint-types",
    },
    {
      key: "purposes",
      label: "Visitor Purposes",
      endpoint: "/purposes",
      testid: "fo-purposes",
    },
  ];
  const cur = tabs.find((t) => t.key === tab);
  return (
    <div data-testid="frontoffice-masters">
      <div className="border-b border-slate-200 mb-3 flex flex-wrap gap-1 text-xs">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`fo-mst-tab-${t.key}`}
            className={`px-3 py-1.5 border-b-2 -mb-px ${tab === t.key ? "border-blue-600 text-blue-600 font-medium" : "border-transparent text-slate-600 hover:text-slate-900"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <MasterCrudPage
        key={cur.key}
        title={cur.label}
        endpoint={cur.endpoint}
        testid={cur.testid}
        fields={[
          { key: "name", label: "Name", required: true },
          {
            key: "description",
            label: "Description",
            type: "textarea",
            full: true,
          },
        ]}
        columns={[
          { key: "name", label: "Name" },
          { key: "description", label: "Description" },
        ]}
      />
    </div>
  );
}
